import { ArrowLeftOutlined, LinkOutlined } from '@ant-design/icons';
import { Alert, App as AntApp, Avatar, Button, Form, Input, InputNumber, List, Modal, Space, Spin, Tag, Typography, Upload } from 'antd';
import { useEffect, useRef, useState } from 'react';

import type { PaymentMethod } from '../../../src/types.ts';
import { api, errorText } from '../lib/api.ts';

interface Props {
  open: boolean;
  currency: string;
  onClose: () => void;
  /** Called whenever a top-up may have changed the balance. */
  onChanged: () => void;
}

type Raw = Record<string, unknown>;

interface Field {
  name: string;
  label: string;
  kind?: 'text' | 'phone' | 'amount' | 'number';
  hint?: string;
}

interface Flow {
  /** Fields for the first step. */
  fields: Field[];
  /** What happens after the first step. */
  first: (values: Raw) => Promise<Raw | string | void>;
  /** Present when the provider needs an OTP / PIN second step. */
  second?: { fields: Field[]; label: string; run: (values: Raw, first: Raw) => Promise<unknown> };
  /** For redirect gateways: pick the payment link out of the init response. */
  link?: (r: Raw) => string | undefined;
  /** For gateways we poll: pick the transaction reference to watch. */
  reference?: (r: Raw) => string | undefined;
  note?: string;
}

const AMOUNT: Field = { name: 'amount', label: 'Amount', kind: 'amount' };
const PHONE: Field = { name: 'mobileNumber', label: 'Mobile number', kind: 'phone', hint: 'The number registered with the payment service' };
const ID_CARD: Field = { name: 'identityCard', label: 'Payment ID card number', kind: 'number', hint: 'The card / account number the service issued you' };
const OTP: Field = { name: 'otp', label: 'Confirmation code', kind: 'number' };

const call = (action: string, body: unknown) => api.paymentCall<Raw>(action, body);

/**
 * One flow per etravel payment type, with the exact endpoints and field names
 * the etravel website sends. Gateways either redirect (we open the link in a new
 * tab), take an OTP (two-step form), or hand back a reference we poll.
 */
function flowFor(m: PaymentMethod, currency: string): Flow | null {
  const t = m.paymentType;
  const otp2 = (init: string, pay: string, extra: Field[] = [], key = 'transactionId'): Flow => ({
    fields: [...extra, AMOUNT],
    first: async (v) => {
      const r = await call(init, v);
      return { [key]: r?.id ?? r?.[key] ?? r?.transactionId };
    },
    second: { fields: [OTP], label: 'Confirm', run: (v, first) => call(pay, { ...v, ...first }) },
  });
  switch (t) {
    case 'MOAMALAT':
      return { fields: [AMOUNT], first: (v) => call('moamalat-pay-init', { lydAmount: v.amount }), link: (r) => str(r.paymentLink) };
    case 'TADAWUL':
      return {
        fields: [AMOUNT, { name: 'customerEmail', label: 'Email' }, { name: 'customerPhone', label: 'Phone', kind: 'phone' }],
        first: (v) => call('tadawul-request', v).then((r) => (typeof r === 'string' ? (JSON.parse(r) as Raw) : r)),
        link: (r) => str(r.url),
      };
    case 'MOBICASH':
      return {
        fields: [AMOUNT, { name: 'customerEmail', label: 'Email' }, { name: 'customerPhone', label: 'Phone', kind: 'phone' }],
        first: (v) => call('mobicash-request', v),
        link: (r) => (r.result === 'success' ? str(r.url) : undefined),
      };
    case 'SADAD':
      return {
        fields: [AMOUNT, { name: 'birthYear', label: 'Birth year', kind: 'number' }, PHONE],
        first: async (v) => {
          const r = await call('sadad-api-otp-request', v);
          const id = (r?.result as Raw | undefined)?.transactionId;
          if (!id) throw new Error('Sadad did not return a transaction id.');
          return { ...v, sadadTransactionId: id };
        },
        second: { fields: [{ name: 'code', label: 'Sadad code', kind: 'number' }], label: 'Pay', run: (v, first) => call('sadad-api-pay', { ...v, ...first }) },
      };
    case 'ADFALI':
      return {
        fields: [AMOUNT, PHONE],
        first: async (v) => {
          const r = await call('edfali-api-otp-request', v);
          return { ...r, sessionID: r?.sessionNumber, ...v };
        },
        second: { fields: [{ name: 'pin', label: 'PIN from SMS', kind: 'number' }], label: 'Pay', run: (v, first) => call('edfali-api-pay', { ...v, ...first }) },
      };
    case 'YUSSOR':
      return otp2('yussor-otp-request', 'yussor-api-pay', [ID_CARD]);
    case 'MASRIFI':
      return otp2('masrifi-otp-request', 'masrifi-api-pay', [ID_CARD]);
    case 'MOBICASH_API':
      return otp2('mobicash-api-otp-request', 'mobicash-api-pay', [ID_CARD]);
    case 'SAHARA_PAY':
      return otp2('sahara-otp-request', 'sahara-api-pay', [ID_CARD]);
    case 'LIBYANA_DCB':
      return otp2('libyana-dcb-otp-request', 'libyana-dcb-pay', [{ ...PHONE, label: 'Libyana number' }]);
    case 'MIZA':
      return {
        fields: [AMOUNT, { name: 'rechargeCode', label: 'Recharge code' }, { name: 'codePin', label: 'Code PIN' }],
        first: (v) => call('uni-pay', v),
      };
    case 'MASTERCARD':
      return {
        fields: [AMOUNT],
        first: (v) => call('mastercard-pay-init', { paymentType: 'MASTERCARD', amount: v.amount, currency }),
        link: (r) => str(r.paymentLink),
        reference: (r) => str(r.transactionReference),
      };
    case 'BINANCE':
    case 'COINBASE':
      return {
        fields: [AMOUNT],
        first: (v) => call('crypto-charge', { paymentType: t, amount: v.amount, currency }),
        link: (r) => str(r.chargeUrl),
        reference: (r) => str(r.transactionReference),
        note: currency === 'USD' ? undefined : 'Amount is converted to USDT at etravel’s current rate.',
      };
    case 'LY_PAY':
      return { fields: [AMOUNT], first: () => Promise.resolve(), note: 'Transfer to the IBAN below, then upload the receipt. etravel reviews it manually.' };
    default:
      return null;
  }
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

function FieldInput({ f }: { f: Field }) {
  if (f.kind === 'amount') return <InputNumber min={1} precision={2} style={{ width: '100%' }} placeholder="0.00" />;
  if (f.kind === 'phone') return <Input placeholder="09XXXXXXXX" inputMode="tel" />;
  if (f.kind === 'number') return <Input inputMode="numeric" />;
  return <Input />;
}

/** "Add funds": choose an etravel payment method, then follow its own flow. */
export function AddFundsModal({ open, currency, onClose, onChanged }: Props) {
  const { message } = AntApp.useApp();
  const [methods, setMethods] = useState<PaymentMethod[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [stage, setStage] = useState<'form' | 'second' | 'wait' | 'done'>('form');
  const [busy, setBusy] = useState(false);
  const [first, setFirst] = useState<Raw>({});
  const [link, setLink] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const poll = useRef<ReturnType<typeof setInterval> | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    setMethod(null);
    setStage('form');
    setLink(null);
    setStatus(null);
    setFile(null);
    setFirst({});
    if (methods) return;
    api
      .paymentMethods()
      .then((list) => setMethods((Array.isArray(list) ? list : []).filter((m) => m.isActive !== false).sort((a, b) => (a.menuOrder ?? 0) - (b.menuOrder ?? 0))))
      .catch((e) => setError(errorText(e)));
  }, [open, methods]);

  useEffect(
    () => () => {
      if (poll.current) clearInterval(poll.current);
    },
    [],
  );

  const stopPolling = () => {
    if (poll.current) clearInterval(poll.current);
    poll.current = null;
  };

  const watch = (reference: string) => {
    stopPolling();
    setStage('wait');
    const tick = async () => {
      try {
        const t = await api.transactionStatus(reference);
        setStatus(t.transactionStatus ?? null);
        if (t.transactionStatus && t.transactionStatus !== 'PENDING_PAYMENT') {
          stopPolling();
          if (t.transactionStatus === 'PAYED') {
            setStage('done');
            onChanged();
          }
        }
      } catch {
        /* keep polling */
      }
    };
    void tick();
    poll.current = setInterval(() => void tick(), 7000);
  };

  const flow = method ? flowFor(method, currency) : null;

  const runFirst = async (values: Raw) => {
    if (!method || !flow) return;
    setBusy(true);
    try {
      if (method.paymentType === 'LY_PAY') {
        if (!file) throw new Error('Attach the transfer receipt image.');
        await api.lyPayInit(Number(values.amount), file);
        message.success('Transfer submitted for review. etravel will credit the wallet once it is checked.');
        setStage('done');
        onChanged();
        return;
      }
      const r = (await flow.first(values)) ?? {};
      const resp = (typeof r === 'object' ? r : {}) as Raw;
      setFirst(resp);
      const url = flow.link?.(resp);
      const ref = flow.reference?.(resp);
      if (url) {
        setLink(url);
        window.open(url, '_blank', 'noopener');
      }
      if (flow.second) setStage('second');
      else if (ref) watch(ref);
      else if (url) setStage('wait');
      else {
        message.success('Payment accepted.');
        setStage('done');
        onChanged();
      }
      if (url && !ref) setStatus(null);
    } catch (err) {
      message.error(errorText(err), 8);
    } finally {
      setBusy(false);
    }
  };

  const runSecond = async (values: Raw) => {
    if (!flow?.second) return;
    setBusy(true);
    try {
      await flow.second.run(values, first);
      message.success('Top-up completed.');
      setStage('done');
      onChanged();
    } catch (err) {
      message.error(errorText(err), 8);
    } finally {
      setBusy(false);
    }
  };

  const iban = method?.details?.match(/LY[0-9]{23}/)?.[0];

  return (
    <Modal
      open={open}
      onCancel={() => {
        stopPolling();
        onClose();
      }}
      footer={null}
      width={620}
      destroyOnHidden
      title={
        <Space>
          {method && (
            <Button size="small" type="text" icon={<ArrowLeftOutlined />} onClick={() => { stopPolling(); setMethod(null); setStage('form'); form.resetFields(); }} />
          )}
          <span>{method ? `Add funds · ${method.nameEn ?? method.paymentType}` : 'Add funds to your wallet'}</span>
        </Space>
      }
    >
      {error && <Alert type="error" showIcon message={error} />}
      {!method && !methods && !error && <Spin />}
      {!method && methods && (
        <List
          dataSource={methods}
          locale={{ emptyText: 'etravel lists no payment methods right now.' }}
          renderItem={(m) => {
            const supported = flowFor(m, currency) !== null || ['CASH', 'STRIPE', 'PAYPAL', 'CREDIT'].includes(m.paymentType);
            return (
              <List.Item
                className={`pay-method${supported ? '' : ' pay-method--off'}`}
                onClick={() => supported && setMethod(m)}
                extra={
                  m.feePercentage ? (
                    <Tag bordered={false} color="orange">
                      {m.feePercentage}% fee
                    </Tag>
                  ) : (
                    <Tag bordered={false} color="green">
                      no fee
                    </Tag>
                  )
                }
              >
                <List.Item.Meta
                  avatar={<Avatar shape="square" src={m.imageUrl ?? undefined}>{(m.nameEn ?? m.paymentType).slice(0, 2)}</Avatar>}
                  title={m.nameEn ?? m.paymentType}
                  description={supported ? m.paymentType.replace(/_/g, ' ').toLowerCase() : 'Not supported in this app — use etravel.ly'}
                />
              </List.Item>
            );
          }}
        />
      )}

      {method && method.paymentType === 'CASH' && (
        <Alert type="info" showIcon message="Cash top-ups are done at an etravel branch or agent." description="Show them your account email or phone number and they will credit the wallet." />
      )}
      {method && (method.paymentType === 'STRIPE' || method.paymentType === 'PAYPAL' || method.paymentType === 'CREDIT') && (
        <Alert
          type="info"
          showIcon
          message={
            method.paymentType === 'CREDIT'
              ? 'Cash & credit top-ups are requested on etravel.ly and approved by etravel staff.'
              : `${method.nameEn ?? method.paymentType} payments need etravel's own checkout page.`
          }
          action={
            <Button size="small" href="https://etravel.ly/account/wallet/charge" target="_blank" rel="noreferrer" icon={<LinkOutlined />}>
              Open etravel.ly
            </Button>
          }
        />
      )}

      {method && flow && stage === 'form' && (
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          {flow.note && <Alert type="warning" showIcon message={flow.note} />}
          {method.paymentType === 'LY_PAY' && method.details && (
            <Alert
              type="info"
              message={
                <div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{method.details}</div>
                  {iban && (
                    <Typography.Text strong copyable>
                      {iban}
                    </Typography.Text>
                  )}
                </div>
              }
            />
          )}
          {method.feePercentage ? <Alert type="warning" showIcon message={`This method charges a ${method.feePercentage}% fee on top of the amount.`} /> : null}
          <Alert type="error" showIcon message="Only use payment accounts in your own name — etravel blocks accounts that use unauthorised payment methods." />
          <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => void runFirst(v as Raw)}>
            {flow.fields.map((f) => (
              <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.hint} rules={[{ required: true, message: 'Required' }]}>
                <FieldInput f={f} />
              </Form.Item>
            ))}
            {method.paymentType === 'LY_PAY' && (
              <Form.Item label="Transfer receipt" required>
                <Upload
                  accept="image/*"
                  maxCount={1}
                  beforeUpload={(f) => {
                    setFile(f);
                    return false;
                  }}
                  onRemove={() => setFile(null)}
                >
                  <Button>Choose image</Button>
                </Upload>
              </Form.Item>
            )}
            <Button type="primary" htmlType="submit" block loading={busy}>
              {flow.second ? 'Send code' : flow.link ? 'Continue to payment' : 'Confirm payment'}
            </Button>
          </Form>
        </Space>
      )}

      {method && flow?.second && stage === 'second' && (
        <Form layout="vertical" requiredMark={false} onFinish={(v) => void runSecond(v as Raw)}>
          <Alert type="success" showIcon message="Code sent. Enter it below to complete the top-up." style={{ marginBottom: 12 }} />
          {flow.second.fields.map((f) => (
            <Form.Item key={f.name} name={f.name} label={f.label} rules={[{ required: true, message: 'Required' }]}>
              <FieldInput f={f} />
            </Form.Item>
          ))}
          <Button type="primary" htmlType="submit" block loading={busy}>
            {flow.second.label}
          </Button>
        </Form>
      )}

      {stage === 'wait' && (
        <Space orientation="vertical" size={12} style={{ width: '100%', textAlign: 'center' }}>
          <Spin />
          <Typography.Text>Complete the payment in the tab that just opened.</Typography.Text>
          {link && (
            <Button href={link} target="_blank" rel="noreferrer" icon={<LinkOutlined />}>
              Open the payment page again
            </Button>
          )}
          {status && <Tag color={status === 'PAYED' ? 'green' : 'processing'}>{status.replace(/_/g, ' ').toLowerCase()}</Tag>}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {flow?.reference ? 'This window updates itself once etravel sees the payment.' : 'When you are done, close this and refresh the balance.'}
          </Typography.Text>
          <Button onClick={() => { stopPolling(); onChanged(); onClose(); }}>I’m done</Button>
        </Space>
      )}

      {stage === 'done' && (
        <Space orientation="vertical" size={12} style={{ width: '100%', textAlign: 'center' }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Done
          </Typography.Title>
          <Typography.Text type="secondary">The wallet balance updates as soon as etravel records the payment.</Typography.Text>
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        </Space>
      )}
    </Modal>
  );
}
